import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Modal from 'components/Modal'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Tooltip } from '@mui/material'
import styled from 'styled-components'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { selectSchoolSubjects } from 'modules/settings/ducks/selectors'
import { selectTeachersList } from 'modules/teachers/ducks/selectors'
import { buildMessageForTeacherBefore, buildMessageForTeacherAfter } from 'modules/newStudents/utils/messageTemplates'
import { copyToClipboard } from 'utils/date'

const MessageForTeacherModal = ({ student }) => {
  const [open, setOpen] = useState(false)
  const schoolSubjects = useSelector(selectSchoolSubjects)
  const teachersList = useSelector(selectTeachersList)

  const { messageBefore, messageAfter } = useMemo(() => ({
    messageBefore: buildMessageForTeacherBefore(student),
    messageAfter: buildMessageForTeacherAfter(student, schoolSubjects, teachersList)
  }), [student, schoolSubjects, teachersList])

  const handleOpen = e => {
    e.stopPropagation()
    setOpen(true)
  }

  const handleClose = e => {
    e && e.stopPropagation()
    setOpen(false)
  }

  const handleCopyBefore = e => {
    e.stopPropagation()
    copyToClipboard(messageBefore)()
  }

  const handleCopyAfter = e => {
    e.stopPropagation()
    copyToClipboard(messageAfter)()
  }

  return (
    <>
      <Trigger onClick={handleOpen}>
        <Tooltip title='Wiadomość dla nauczyciela'>
          <StyledIcon />
        </Tooltip>
      </Trigger>
      <Modal
        title='Wiadomość dla nauczyciela'
        open={open}
        onClose={handleClose}
      >
        <Content>
          <TemplateBlock>
            <TemplateHeader>
              <TemplateTitle>Przed ustawieniem terminów</TemplateTitle>
              <CopyButton type='button' onClick={handleCopyBefore} title='Skopiuj'>
                <ContentCopyIcon fontSize='small' />
              </CopyButton>
            </TemplateHeader>
            <TemplatePreview>{messageBefore}</TemplatePreview>
          </TemplateBlock>
          <TemplateBlock>
            <TemplateHeader>
              <TemplateTitle>Po ustawieniu terminów</TemplateTitle>
              <CopyButton type='button' onClick={handleCopyAfter} title='Skopiuj'>
                <ContentCopyIcon fontSize='small' />
              </CopyButton>
            </TemplateHeader>
            <TemplatePreview>{messageAfter}</TemplatePreview>
          </TemplateBlock>
        </Content>
      </Modal>
    </>
  )
}

export default MessageForTeacherModal

const Trigger = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`

const StyledIcon = styled(PersonOutlineIcon)`
  transition: all .3s;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary.main};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 400px;
`

const TemplateBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border || '#ccc'};
  border-radius: 8px;
  overflow: hidden;
`

const TemplateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f5f5f5'};
`

const TemplateTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
`

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  color: ${({ theme }) => theme.colors.darkGrey};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary.main};
  }
`

const TemplatePreview = styled.pre`
  margin: 0;
  padding: 16px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
`
